import React, { useEffect, useState, useCallback } from "react";
import "../assets/AllTabsCommon.css";
import "../assets/StartRemoteDebug.css";
import "../assets/ADAssociate.css";
import Select from "react-select";
import { fetchData, postData } from "../api/restapi";
import ContextCollectionRules from "./ContextCollectionRules";
import TelemetryCollectorConfig from "./TelemetryCollectorConfig";
import ADLogCodeListManagement from "./ADLogCodeListManagement";

const isVersionAtLeast2 = (version) => {
  if (!version) return false;
  const major = parseInt(version.split('.')[0], 10);
  return !isNaN(major) && major >= 2;
};

const timeToSecondsFromDayStart = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return ((h || 0) * 3600) + ((m || 0) * 60);
};

const computeDayMask = (activeDays) => {
  const bits = { mon: 1, tue: 2, wed: 4, thu: 8, fri: 16, sat: 32, sun: 64 };
  return Object.entries(bits).reduce((mask, [d, b]) => activeDays[d] ? mask | b : mask, 0);
};

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const emptySchedule = () => ({
  applyToRules: [],
  activeDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
  startTime: '',
  durationMins: '',
  windowDuration: '',
});

const COMMAND_OPTIONS_ODL = [
  { value: "CONFIGURE_ENABLE", label: "CONFIGURE_ENABLE" },
  { value: "ENABLE", label: "ENABLE" },
];

const COMMAND_OPTIONS_DEFAULT = [
  { value: "ENABLE", label: "ENABLE" },
];

// react-select styles tuned to match ADAssociate.css .inp height (~33px)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '33px',
    borderColor: state.isFocused ? '#1a56db' : '#d1d5db',
    borderRadius: '6px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26,86,219,.08)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#1a56db' : '#9ca3af' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#1f2937',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '13px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    color: state.isSelected ? 'white' : '#1f2937',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? '#1a56db'
      : state.isFocused
      ? '#f0f4ff'
      : 'white',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const selectStylesMulti = {
  ...selectStyles,
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
};

function StartRemoteDebugTab({ userInfo, prefilledSystemId, setPrefilledSystemId, hideSystemId }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [rows, setRows] = useState([{ diagnosticData: null, selectedLogMask: null, maxFileSizeMb: "", command: null }]);
  const [inputText, setInputText] = useState("");
  const [sessionTimer, setSessionTimer] = useState("");
  const [uploadFrequency, setUploadFrequency] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [diagnosticDataOptions, setDiagnosticDataOptions] = useState([]);
  const [logMaskDescriptions, setLogMaskDescriptions] = useState([]);
  const [sessionMode, setSessionMode] = useState({ value: "immediate", label: "Start Immediately" });
  const [schedulingMode, setSchedulingMode] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("");

  // Open ended
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [isOpenEndedDisabled, setIsOpenEndedDisabled] = useState(true);

  // Context collection
  const [contextCollectionRuleOptions, setContextCollectionRuleOptions] = useState([]);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState("");
  const [contextRules, setContextRules] = useState([]);
  const [collectionSchedule, setCollectionSchedule] = useState(emptySchedule());
  const [showRulesBrowser, setShowRulesBrowser] = useState(false);
  const [showLogCodesBrowser, setShowLogCodesBrowser] = useState(false);

  // Session-level Log Mask (mandatory for ODL + CONFIGURE_ENABLE, all device versions)
  const [sessionLogMask, setSessionLogMask] = useState(null);

  // Device version state for determining which flow to show
  const [deviceVersion, setDeviceVersion] = useState(null);

  // AD (Telemetry Collector Config) payload — produced by TelemetryCollectorConfig child
  const [adPayload, setAdPayload] = useState(null);
  const [adCollectorType, setAdCollectorType] = useState('');
  const [adCommand, setAdCommand] = useState('');
  const [adEntries, setAdEntries] = useState([]);

  useEffect(() => {
    const fetchDiagnosticData = async () => {
      try {
        const data = await fetchData("/api/v1/diagnostic-data");
        if (data.statusCode === 200) {
          const options = data.collectorIds.map((collector) => ({
            value: collector.id,
            label: collector.name,
          }));
          setDiagnosticDataOptions(options);
        }
      } catch (err) {}
    };

    const fetchLogMaskDescriptions = async () => {
      try {
        const data = await fetchData("/api/v1/onboarding/log-mask-descriptions");
        if (data.logMaskDescriptions) {
          const options = data.logMaskDescriptions.map((mask) => ({
            value: mask.id,
            label: mask.description,
            logCodesIndex: mask.logCodesIndex,
          }));
          setLogMaskDescriptions(options);
        }
      } catch (err) {}
    };

    const fetchContextCollectionRules = async () => {
      try {
        const data = await fetchData("/api/v1/onboarding/context-collection-rules");
        if (data.rules) {
          const options = data.rules.map((rule) => ({
            value: rule.id,
            label: rule.label,
            ruleType: rule.ruleType,
            ruleData: rule,
          }));
          setContextCollectionRuleOptions(options);
        }
      } catch (err) {}
    };

    fetchDiagnosticData();
    fetchLogMaskDescriptions();
    fetchContextCollectionRules();
  }, []);

  useEffect(() => {
    if (prefilledSystemId) {
      setInputText(prefilledSystemId);
      if (setPrefilledSystemId) setPrefilledSystemId("");

      const fetchDeviceVersion = async () => {
        try {
          const statusResponse = await fetchData(`/api/v1/device-current-status/${prefilledSystemId}`);
          if (statusResponse.statusCode === 200) {
            const version = statusResponse.currentClientStatus?.version;
            setDeviceVersion(version);
            if (isVersionAtLeast2(version)) {
              setIsOpenEndedDisabled(false);
            } else {
              setIsOpenEndedDisabled(true);
              setIsOpenEnded(false);
            }
          } else {
            setDeviceVersion(null);
            setIsOpenEndedDisabled(true);
            setIsOpenEnded(false);
          }
        } catch (err) {
          setDeviceVersion(null);
          setIsOpenEndedDisabled(true);
        }
      };
      fetchDeviceVersion();
    }
  }, [prefilledSystemId, setPrefilledSystemId]);

  const handleSystemIdBlur = async () => {
    if (!inputText.trim()) return;
    try {
      const statusResponse = await fetchData(`/api/v1/device-current-status/${inputText}`);
      if (statusResponse.statusCode === 200) {
        const version = statusResponse.currentClientStatus?.version;
        setDeviceVersion(version);
        if (isVersionAtLeast2(version)) {
          setIsOpenEndedDisabled(false);
        } else {
          setIsOpenEndedDisabled(true);
          setIsOpenEnded(false);
        }
      } else {
        setDeviceVersion(null);
        setIsOpenEndedDisabled(true);
        setIsOpenEnded(false);
      }
    } catch (err) {
      setDeviceVersion(null);
      setIsOpenEndedDisabled(true);
    }
  };

  const isContextCollectionActive = () =>
    rows.some(row => row.diagnosticData?.label === "ODL" && row.command?.value === "CONFIGURE_ENABLE");

  const shouldShowContextCollection = () => {
    if (!deviceVersion) return true;
    return isVersionAtLeast2(deviceVersion);
  };

  const resetContextCollection = () => {
    setMaxFileSizeMb("");
    setContextRules([]);
    setCollectionSchedule(emptySchedule());
    setSessionLogMask(null);
  };

  const handleAdPayloadChange = useCallback((payload) => {
    setAdPayload(payload);
  }, []);

  const isFormValid = () => {
    const hasSystemId = inputText.trim() !== "";
    const hasTimer = isOpenEnded || sessionTimer.trim() !== "";
    const hasUploadFreq = uploadFrequency.trim() !== "";
    const hasDiagData = rows.some((row) => row.diagnosticData) || adPayload !== null;
    const rowsValid = rows.every((row) => {
      if (!row.diagnosticData) return true; // empty RD row is fine if AD payload covers it
      if (!row.command) return false;
      if (row.diagnosticData.label === "ODL" && row.command.value === "CONFIGURE_ENABLE") {
        if (!sessionLogMask) return false;
        if (shouldShowContextCollection()) {
          return maxFileSizeMb.trim() !== "" &&
            contextRules.every(r => r.contextCollectionRule && r.diagConfig);
        } else {
          return row.maxFileSizeMb.trim() !== "";
        }
      }
      return true;
    });

    const baseValidation = hasSystemId && hasTimer && hasUploadFreq && hasDiagData && rowsValid;

    const adValid = adPayload === null || !!(
      adPayload.adCommand && (
        adPayload.adCommand !== 'CONFIGURE_ENABLE' || (
          adEntries.filter(e => e.adConfig).length > 0 &&
          adEntries.filter(e => e.adConfig).every(e =>
            e.rules.some(r => r.triggerType === 'MAIN')
          )
        )
      )
    );

    console.log('[Continue] isFormValid check:', {
      hasSystemId, hasTimer, hasUploadFreq, hasDiagData, rowsValid, adValid,
      adPayload, sessionMode: sessionMode?.value,
    });

    if (sessionMode?.value === "scheduled") {
      return baseValidation && adValid && schedulingMode && scheduledTime.trim() !== "";
    }
    return baseValidation && adValid;
  };

  const buildContextCollectionPayload = () => {
    const payload = {
      odlMode: "ODL_CONTEXT_COLLECTION",
      maxFileSizeMb: parseInt(maxFileSizeMb),
      ruleConfigurations: contextRules
        .filter(r => r.contextCollectionRule && r.diagConfig)
        .map(r => ({
          contextCollectionRuleId: r.contextCollectionRule.value,
          logCodesIndex: r.diagConfig.logCodesIndex,
        })),
    };

    const hasSchedule =
      collectionSchedule.startTime !== '' ||
      collectionSchedule.durationMins !== '' ||
      collectionSchedule.applyToRules.length > 0 ||
      Object.values(collectionSchedule.activeDays).some(v => v);

    if (hasSchedule) {
      payload.collectionSchedules = [{
        contextCollectionRuleIds: collectionSchedule.applyToRules.map(r => r.value),
        startTime: timeToSecondsFromDayStart(collectionSchedule.startTime),
        duration: parseInt(collectionSchedule.durationMins || 0) * 60,
        dayMask: computeDayMask(collectionSchedule.activeDays),
        windowDuration: collectionSchedule.windowDuration !== '' ? parseInt(collectionSchedule.windowDuration) * 60 : null,
      }];
    }
    return payload;
  };

  const buildCollectorMappings = () => {
    const rdMappings = rows
      .filter(row => row.diagnosticData)
      .map((row) => ({
        collectorId: row.diagnosticData.value,
        commandName: row.command?.value,
      }));

    if (adPayload) {
      return [
        ...rdMappings,
        { collectorId: adPayload.adCollectorId, commandName: adPayload.adCommand },
      ];
    }
    return rdMappings;
  };

  const handleContinue = async () => {
    setValidationError("");

    if (!isOpenEnded && uploadFrequency.trim() !== "" && sessionTimer.trim() !== "" &&
        parseInt(uploadFrequency) >= parseInt(sessionTimer) * 60) {
      setValidationError("Upload Frequency must be less than Session Timer.");
      return;
    }

    if (sessionMode?.value === "scheduled") {
      setCurrentStep(2);
      return;
    }

    try {
      const statusResponse = await fetchData(`/api/v1/device-current-status/${inputText}`);
      if (statusResponse.statusCode === 400) {
        setValidationError(`No client found for given System ID: ${inputText}`);
      } else {
        setCurrentStep(2);
      }
    } catch (error) {
      setValidationError(`Error checking client status: ${error.message}`);
    }
  };

  const handleGoBack = () => {
    setCurrentStep(1);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const odlRow = rows.find(row => row.diagnosticData?.label === "ODL");

    if (sessionMode?.value === "scheduled") {
      const scheduledTimeUTC = new Date(scheduledTime).toISOString();

      const requestBody = {
        systemId: inputText,
        scheduledTime: scheduledTimeUTC,
        schedulingMode: schedulingMode.value,
        sessionTimeInMins: parseInt(sessionTimer),
        scheduledBy: userInfo,
        wbcCollectorCommandNameMappings: buildCollectorMappings(),
        sessionType: "WBC_START_SESSION",
        uploadReportFreqInSec: parseInt(uploadFrequency),
        isOpenEnded: isOpenEnded,
      };

      if (odlRow && odlRow.command?.value === "CONFIGURE_ENABLE") {
        if (shouldShowContextCollection()) {
          Object.assign(requestBody, buildContextCollectionPayload());
          requestBody.logMaskId = sessionLogMask?.value;
        } else {
          requestBody.odlMode = "ODL_LOG_MASK";
          requestBody.logMaskId = sessionLogMask?.value;
          requestBody.maxFileSizeMb = parseInt(odlRow.maxFileSizeMb);
        }
      } else if (odlRow && shouldShowContextCollection()) {
        requestBody.odlMode = "ODL_CONTEXT_COLLECTION";
      }

      if (adPayload && adPayload.adCollectorConfigs && adPayload.adCollectorConfigs.length > 0) {
        requestBody.adCollectorConfigs = adPayload.adCollectorConfigs;
      }

      try {
        const response = await postData("/api/v1/schedule-rd-session", requestBody);
        if (response.statusCode === 200 || response.statusCode === 201) {
          setSuccessMessage(`Remote Diagnostics Management session scheduled successfully!\n\nSession ID: ${response.sessionId}`);
        } else {
          setErrorMessage(response.errorMessage || "Failed to schedule Remote Diagnostics Management session.");
        }
      } catch (error) {
        setErrorMessage("Failed to schedule Remote Diagnostics Management session.");
      }
    } else {
      const requestBody = {
        systemId: inputText,
        sessionType: "WBC_START_SESSION",
        wbcCollectorCommandNameMappings: buildCollectorMappings(),
        sessionTimeInMins: isOpenEnded ? null : parseInt(sessionTimer),
        uploadReportFreqInSec: parseInt(uploadFrequency),
        user: userInfo,
        isOpenEnded: isOpenEnded,
      };

      if (odlRow && odlRow.command?.value === "CONFIGURE_ENABLE") {
        if (shouldShowContextCollection()) {
          Object.assign(requestBody, buildContextCollectionPayload());
          requestBody.logMaskId = sessionLogMask?.value;
        } else {
          requestBody.odlMode = "ODL_LOG_MASK";
          requestBody.logMaskId = sessionLogMask?.value;
          requestBody.maxFileSizeMb = parseInt(odlRow.maxFileSizeMb);
        }
      } else if (odlRow && shouldShowContextCollection()) {
        requestBody.odlMode = "ODL_CONTEXT_COLLECTION";
      }

      if (adPayload && adPayload.adCollectorConfigs && adPayload.adCollectorConfigs.length > 0) {
        requestBody.adCollectorConfigs = adPayload.adCollectorConfigs;
      }

      try {
        const response = await postData("/api/v1/configure-diagnostics", requestBody);
        if (response.statusCode === 200 || response.statusCode === 201) {
          setSuccessMessage(`Remote Diagnostics Management session started successfully!\n\nSession ID: ${response.sessionId}`);
        } else {
          setErrorMessage(response.errorMessage || "Failed to start Remote Diagnostics Management session.");
        }
      } catch (error) {
        setErrorMessage("Failed to start Remote Diagnostics Management session.");
      }
    }
  };

  const handleStartNew = () => {
    setCurrentStep(1);
    setSuccessMessage("");
    setErrorMessage("");
    setValidationError("");
    setInputText("");
    setSessionTimer("");
    setUploadFrequency("");
    setRows([{ diagnosticData: null, selectedLogMask: null, maxFileSizeMb: "", command: null }]);
    setSessionMode({ value: "immediate", label: "Start Immediately" });
    setSchedulingMode(null);
    setScheduledTime("");
    setIsOpenEnded(false);
    setIsOpenEndedDisabled(true);
    setDeviceVersion(null);
    setAdPayload(null);
    setAdCollectorType('');
    setAdCommand('');
    setAdEntries([]);
    resetContextCollection();
  };

  const handleAddRow = () => {
    if (getRemainingOptionsCount() > 0) {
      setRows([...rows, { diagnosticData: null, selectedLogMask: null, maxFileSizeMb: "", command: null }]);
    }
  };

  const handleRemoveRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
  };

  const handleRowChange = (index, field, value) => {
    const oldRow = rows[index];
    const updatedRow = { ...oldRow, [field]: value };

    const wasContextCollection = oldRow.diagnosticData?.label === "ODL" && oldRow.command?.value === "CONFIGURE_ENABLE";
    const isContextCollection = updatedRow.diagnosticData?.label === "ODL" && updatedRow.command?.value === "CONFIGURE_ENABLE";

    if (wasContextCollection && !isContextCollection) {
      resetContextCollection();
    }

    setRows(rows.map((row, i) => i === index ? updatedRow : row));
  };

  const getFilteredOptions = (index) => {
    const selectedValues = rows
      .map((row, i) => (i !== index ? row.diagnosticData?.value : null))
      .filter((value) => value !== null);
    return diagnosticDataOptions.filter((option) =>
      option.label !== 'AD' && !selectedValues.includes(option.value)
    );
  };

  const getRemainingOptionsCount = () => {
    const selectedValues = rows.map((row) => row.diagnosticData?.value).filter((value) => value !== null);
    return diagnosticDataOptions.filter((option) =>
      option.label !== 'AD' && !selectedValues.includes(option.value)
    ).length;
  };

  const addContextRule = () => {
    setContextRules([...contextRules, { contextCollectionRule: null, diagConfig: null, showDetail: false }]);
  };

  const removeContextRule = (index) => {
    const removed = contextRules[index];
    const updated = contextRules.filter((_, i) => i !== index);
    setContextRules(updated);
    if (removed?.contextCollectionRule) {
      setCollectionSchedule(prev => ({
        ...prev,
        applyToRules: prev.applyToRules.filter(r => r.value !== removed.contextCollectionRule.value),
      }));
    }
  };

  const handleContextRuleChange = (index, field, value) => {
    const prev = contextRules[index];
    const updated = contextRules.map((r, i) => {
      if (i !== index) return r;
      const newEntry = { ...r, [field]: value };
      if (field === 'contextCollectionRule') {
        newEntry.showDetail = !!value;
      }
      return newEntry;
    });
    setContextRules(updated);
    if (field === 'contextCollectionRule' && prev?.contextCollectionRule) {
      setCollectionSchedule(s => ({
        ...s,
        applyToRules: s.applyToRules.filter(r => r.value !== prev.contextCollectionRule.value),
      }));
    }
  };

  const handleScheduleChange = (field, value) => {
    setCollectionSchedule(prev => ({ ...prev, [field]: value }));
  };

  const scheduleRuleOptions = contextRules
    .filter(r => r.contextCollectionRule)
    .map(r => ({ value: r.contextCollectionRule.value, label: r.contextCollectionRule.label }));

  // ── Rule details renderer (ADAssociate.css classes) ──────────────────────
  const renderRuleDetails = (ruleData) => {
    if (!ruleData) return null;
    return (
      <div className="rule-details-grid">
        <div className="rule-detail-card info-card">
          <div className="rdc-header">Rule Info</div>
          <div className="rdc-row"><span className="rdc-key">Rule Type</span><span className="rdc-val">{ruleData.ruleType || '—'}</span></div>
          <div className="rdc-row"><span className="rdc-key">Label</span><span className="rdc-val">{ruleData.label || ruleData.name || '—'}</span></div>
        </div>
        {ruleData.hysteresis && (
          <div className="rule-detail-card hyst-card">
            <div className="rdc-header">Hysteresis</div>
            <div className="rdc-row"><span className="rdc-key">Off Hysteresis (ms)</span><span className="rdc-val">{ruleData.hysteresis.off ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">On Hysteresis (ms)</span><span className="rdc-val">{ruleData.hysteresis.on ?? '—'}</span></div>
          </div>
        )}
        {ruleData.trigger && (
          <div className="rule-detail-card trig-card">
            <div className="rdc-header">Trigger</div>
            <div className="rdc-row"><span className="rdc-key">Start Trigger Rule ID</span><span className="rdc-val">{ruleData.trigger.startId ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">Stop Trigger Rule ID</span><span className="rdc-val">{ruleData.trigger.stopId ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">Stop Duration (ms)</span><span className="rdc-val">{ruleData.trigger.stopDuration ?? '—'}</span></div>
          </div>
        )}
        {ruleData.quota && (
          <div className="rule-detail-card quota-card">
            <div className="rdc-header">Quota</div>
            <div className="rdc-row"><span className="rdc-key">Max Size (bytes)</span><span className="rdc-val">{ruleData.quota.maxSize ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">Max Occurrence</span><span className="rdc-val">{ruleData.quota.maxOccurrence ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">Time Window (s)</span><span className="rdc-val">{ruleData.quota.timeWindow ?? '—'}</span></div>
            <div className="rdc-row"><span className="rdc-key">Max Time Window (ms)</span><span className="rdc-val">{ruleData.quota.maxTimeWindow ?? '—'}</span></div>
          </div>
        )}
      </div>
    );
  };

  // ─── STEP 1 ───────────────────────────────────────────────────────────────
  if (currentStep === 1) {
    return (
      <>
        <div className="ad-associate-scope">
          <div className="page">
            <div className="page-title">Start Remote Diagnostics Session</div>
            <div className="page-subtitle">
              Configure Remote Diagnostics (RD) and optionally Anomaly Detection (AD)
              Telemetry Collector settings for this session.
            </div>

            {validationError && (
              <div className="val-banner show">
                <strong>Please fix the following before continuing:</strong>
                <ul><li>{validationError}</li></ul>
              </div>
            )}

            {/* ── SECTION 1: Session Details ──────────────────────────────── */}
            <div className="section-divider">
              <div className="section-divider-line" />
              <div className="section-divider-label">Session Details</div>
              <div className="section-divider-line" />
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-hdr"><h3>Session Setup</h3></div>
              <div className="card-body">
                <div className="form-grid form-grid-2">

                  {/* System ID */}
                  {!hideSystemId && (
                  <div className="field">
                    <label>System ID <span className="req">*</span></label>
                    <input
                      className="inp"
                      type="text"
                      placeholder="Enter System ID"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onBlur={handleSystemIdBlur}
                    />
                  </div>
                  )}

                  {/* Session Mode */}
                  <div className="field">
                    <label>
                      Session Mode
                      {isOpenEndedDisabled && inputText.trim() && (
                        <span style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>
                          (Open Ended requires device v2.0+)
                        </span>
                      )}
                    </label>
                    <div className="seg">
                      {['Time Bound', 'Open Ended'].map((option) => {
                        const isOpenEndedOption = option === 'Open Ended';
                        const isSelected = isOpenEndedOption ? isOpenEnded : !isOpenEnded;
                        const isDisabled = isOpenEndedOption && isOpenEndedDisabled;
                        return (
                          <button
                            key={option}
                            className={`seg-btn${isSelected ? ' active' : ''}`}
                            onClick={() => !isDisabled && setIsOpenEnded(isOpenEndedOption)}
                            disabled={isDisabled}
                            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.45 : 1 }}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Session Type */}
                  <div className="field">
                    <label>Session Type <span className="req">*</span></label>
                    <Select
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      options={[
                        { value: "immediate", label: "Start Immediately" },
                        { value: "scheduled", label: "Schedule" },
                      ]}
                      value={sessionMode}
                      onChange={(value) => setSessionMode(value)}
                      placeholder="Select..."
                    />
                  </div>

                  {/* Session Timer */}
                  <div className="field" style={{ opacity: isOpenEnded ? 0.4 : 1 }}>
                    <label>
                      Session Timer (mins){!isOpenEnded && <span className="req">*</span>}
                    </label>
                    <input
                      className="inp"
                      type="number"
                      min="1"
                      placeholder="Enter time in mins"
                      value={sessionTimer}
                      onChange={(e) => setSessionTimer(e.target.value)}
                      disabled={isOpenEnded}
                      style={{ cursor: isOpenEnded ? 'not-allowed' : 'auto' }}
                    />
                  </div>

                  {/* Upload Frequency */}
                  <div className="field">
                    <label>Upload Frequency (secs) <span className="req">*</span></label>
                    <input
                      className="inp"
                      type="number"
                      min="1"
                      placeholder="Enter time in secs"
                      value={uploadFrequency}
                      onChange={(e) => setUploadFrequency(e.target.value)}
                    />
                  </div>

                  {/* Scheduling Mode + Scheduled Time (only when scheduled) */}
                  {sessionMode?.value === "scheduled" && (
                    <>
                      <div className="field">
                        <label>Scheduling Mode <span className="req">*</span></label>
                        <Select
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          options={[
                            { value: "FIXED", label: "At" },
                            { value: "FLEXIBLE", label: "By" },
                          ]}
                          value={schedulingMode}
                          onChange={(value) => setSchedulingMode(value)}
                          placeholder="Select..."
                        />
                      </div>
                      <div className="field">
                        <label>Scheduled Time <span className="req">*</span></label>
                        <input
                          className="inp"
                          type="datetime-local"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                </div>
              </div>
            </div>

            {/* ── SECTION 2: Configuration ────────────────────────────────── */}
            <div className="section-divider">
              <div className="section-divider-line" />
              <div className="section-divider-label">Configuration</div>
              <div className="section-divider-line" />
            </div>

            {/* RD CONFIG PANEL */}
            <div className="config-panel rd-panel" style={{ marginBottom: 16 }}>
              <div className="config-panel-header">
                <div className="rd-panel-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <div>
                  <div className="rd-panel-title">Remote Diagnostics (RD)</div>
                </div>
                <span className="rd-panel-badge">RD Config</span>
              </div>

              <div className="rd-panel-body">
                {rows.map((row, index) => (
                  <React.Fragment key={index}>
                    {/* Main diag row */}
                    <div className="diag-row">
                      <div className="field">
                        <label>Diagnostic Data <span className="req">*</span></label>
                        <Select
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          options={getFilteredOptions(index)}
                          value={row.diagnosticData}
                          onChange={(value) => handleRowChange(index, "diagnosticData", value)}
                          placeholder="Select..."
                          isClearable
                        />
                      </div>
                      <div className="field">
                        <label>Command <span className="req">*</span></label>
                        <Select
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          options={row.diagnosticData?.label === "ODL" ? COMMAND_OPTIONS_ODL : COMMAND_OPTIONS_DEFAULT}
                          value={row.command}
                          onChange={(value) => handleRowChange(index, "command", value)}
                          placeholder="Select..."
                          isDisabled={!row.diagnosticData}
                        />
                      </div>
                      <div style={{ alignSelf: 'flex-end' }}>
                        {rows.length > 1 && (
                          <button className="diag-remove-btn" onClick={() => handleRemoveRow(index)}>
                            - Remove Diagnostic Data
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ODL CONFIGURE_ENABLE sub-section */}
                    {row.diagnosticData?.label === "ODL" && row.command?.value === "CONFIGURE_ENABLE" && (
                      <div className="diag-row-sub">
                        {shouldShowContextCollection() ? (
                          <>
                            {/* Diag Config + Max File Size + Add CCR button */}
                            <div className="diag-row-sub-grid">
                              <div className="field">
                                <label>Diag Config <span className="req">*</span></label>
                                <Select
                                  styles={selectStyles}
                                  menuPortalTarget={document.body}
                                  options={logMaskDescriptions}
                                  value={sessionLogMask}
                                  onChange={(val) => setSessionLogMask(val)}
                                  placeholder="Select..."
                                />
                              </div>
                              <div className="field">
                                <label>Max File Size (MB) <span className="req">*</span></label>
                                <input
                                  className="inp"
                                  type="number"
                                  placeholder="e.g. 56"
                                  value={maxFileSizeMb}
                                  onChange={(e) => setMaxFileSizeMb(e.target.value)}
                                />
                              </div>
                              {contextRules.length === 0 && (
                                <div style={{ alignSelf: 'flex-end' }}>
                                  <button className="ctx-rule-btn" onClick={addContextRule}>
                                    + Add Context Collection Rule
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Context Collection Rules */}
                            {contextRules.map((ruleEntry, ruleIdx) => {
                              const usedRuleValues = new Set(
                                contextRules
                                  .filter((_, i) => i !== ruleIdx)
                                  .map(r => r.contextCollectionRule?.value)
                                  .filter(Boolean)
                              );
                              const availableRuleOptions = contextCollectionRuleOptions.filter(
                                opt => !usedRuleValues.has(opt.value)
                              );
                              return (
                                <div className="rule-row" key={ruleIdx} style={{ marginTop: 10 }}>
                                  <div className="rule-row-grid">
                                    <div className="field">
                                      <label>
                                        Context Collection Rule
                                        <span
                                          className="ccr-browse"
                                          onClick={() => setShowRulesBrowser(true)}
                                        >
                                          Browse all rules ↗
                                        </span>
                                      </label>
                                      <Select
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        options={availableRuleOptions}
                                        value={ruleEntry.contextCollectionRule}
                                        onChange={(val) => handleContextRuleChange(ruleIdx, 'contextCollectionRule', val)}
                                        placeholder="Select..."
                                        formatOptionLabel={(option, { context }) => {
                                          if (context === 'value') return <span>{option.label}</span>;
                                          return (
                                            <div>
                                              <div>{option.label}</div>
                                              {option.ruleType && (
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{option.ruleType}</div>
                                              )}
                                            </div>
                                          );
                                        }}
                                      />
                                    </div>
                                    <div className="field">
                                      <label>Log Mask</label>
                                      <Select
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        options={logMaskDescriptions}
                                        value={ruleEntry.diagConfig}
                                        onChange={(val) => handleContextRuleChange(ruleIdx, 'diagConfig', val)}
                                        placeholder="Select..."
                                      />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button className="ctx-rule-btn" onClick={addContextRule}>
                                      + Add Context Collection Rule
                                    </button>
                                    <button className="diag-remove-btn" onClick={() => removeContextRule(ruleIdx)}>
                                      - Remove Rule
                                    </button>
                                  </div>
                                  {ruleEntry.contextCollectionRule?.ruleData && (
                                    <div>
                                      <button
                                        className={`rule-detail-toggle${ruleEntry.showDetail ? ' open' : ''}`}
                                        onClick={() => handleContextRuleChange(ruleIdx, 'showDetail', !ruleEntry.showDetail)}
                                      >
                                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                          <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        {ruleEntry.showDetail ? '▲ Hide Rule Details' : '▼ Show Rule Details'}
                                      </button>
                                      {ruleEntry.showDetail && renderRuleDetails(ruleEntry.contextCollectionRule.ruleData)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Collection Schedule */}
                            {contextRules.length > 0 && (
                              <div className="schedule-section" style={{ marginTop: 16 }}>
                                <div className="schedule-title">
                                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                  Collection Schedule
                                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-400)', marginLeft: 4 }}>(Optional)</span>
                                </div>
                                <div className="schedule-grid">
                                  <div className="field">
                                    <label>Apply to Rules</label>
                                    <Select
                                      styles={selectStylesMulti}
                                      menuPortalTarget={document.body}
                                      isMulti
                                      options={scheduleRuleOptions}
                                      value={collectionSchedule.applyToRules}
                                      onChange={(vals) => handleScheduleChange('applyToRules', vals || [])}
                                      placeholder="Select rules..."
                                    />
                                  </div>
                                  <div className="field">
                                    <label>Start Time (UTC)</label>
                                    <input
                                      className="inp"
                                      type="time"
                                      value={collectionSchedule.startTime}
                                      onChange={(e) => handleScheduleChange('startTime', e.target.value)}
                                    />
                                  </div>
                                  <div className="field">
                                    <label>Duration (mins)</label>
                                    <input
                                      className="inp"
                                      type="number"
                                      placeholder="e.g. 30"
                                      value={collectionSchedule.durationMins}
                                      onChange={(e) => handleScheduleChange('durationMins', e.target.value)}
                                    />
                                  </div>
                                  <div className="field">
                                    <label>Window Duration (mins)</label>
                                    <input
                                      className="inp"
                                      type="number"
                                      placeholder="e.g. 60"
                                      value={collectionSchedule.windowDuration}
                                      onChange={(e) => handleScheduleChange('windowDuration', e.target.value)}
                                    />
                                  </div>
                                </div>
                                <div className="active-days-row">
                                  <span className="active-days-label">Active Days</span>
                                  {DAYS.map(day => (
                                    <label className="day-cb" key={day.key}>
                                      <input
                                        type="checkbox"
                                        checked={collectionSchedule.activeDays[day.key]}
                                        onChange={(e) => handleScheduleChange('activeDays', {
                                          ...collectionSchedule.activeDays,
                                          [day.key]: e.target.checked,
                                        })}
                                      />
                                      {day.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          // Legacy flow (device < v2.0) — single log mask
                          <div className="diag-row-sub-grid">
                            <div className="field">
                              <label>Diag Config <span className="req">*</span></label>
                              <Select
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                options={logMaskDescriptions}
                                value={sessionLogMask}
                                onChange={(val) => setSessionLogMask(val)}
                                placeholder="Select..."
                              />
                            </div>
                            <div className="field">
                              <label>Max File Size (MB) <span className="req">*</span></label>
                              <input
                                className="inp"
                                type="number"
                                placeholder="Enter max file size in MB"
                                value={row.maxFileSizeMb}
                                onChange={(e) => handleRowChange(index, "maxFileSizeMb", e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {getRemainingOptionsCount() > 0 && (
                  <button className="add-diag-btn" onClick={handleAddRow}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Diagnostic Data
                  </button>
                )}
              </div>
            </div>

            {/* TELEMETRY COLLECTOR CONFIG PANEL */}
            <TelemetryCollectorConfig
              onPayloadChange={handleAdPayloadChange}
              contextCollectionRuleOptions={contextCollectionRuleOptions}
              diagnosticDataOptions={diagnosticDataOptions}
              onBrowseRules={() => setShowRulesBrowser(true)}
              onBrowseLogCodes={() => setShowLogCodesBrowser(true)}
              collectorType={adCollectorType}
              onCollectorTypeChange={setAdCollectorType}
              adCommand={adCommand}
              onAdCommandChange={setAdCommand}
              adEntries={adEntries}
              onAdEntriesChange={setAdEntries}
            />

          </div>

          {/* FOOTER ACTIONS */}
          <div className="footer-actions">
            <div className="footer-actions-inner">
              <button
                className="btn btn-primary"
                onClick={handleContinue}
                disabled={!isFormValid()}
              >
                Continue →
              </button>
            </div>
          </div>
        </div>

        {/* Rules browser modal — rendered outside ad-associate-scope to avoid CSS reset conflicts */}
        {showRulesBrowser && (
          <div className="modal-overlay" onClick={() => setShowRulesBrowser(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Context Collection Rules</h2>
                <button className="modal-close-button" onClick={() => setShowRulesBrowser(false)}>×</button>
              </div>
              <div className="modal-body">
                <ContextCollectionRules mode="view" />
              </div>
            </div>
          </div>
        )}

        {/* Log code mask browser modal */}
        {showLogCodesBrowser && (
          <div className="modal-overlay" onClick={() => setShowLogCodesBrowser(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>AD Log Codes</h2>
                <button className="modal-close-button" onClick={() => setShowLogCodesBrowser(false)}>×</button>
              </div>
              <div className="modal-body">
                <ADLogCodeListManagement userInfo={userInfo} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── STEP 2: Review Page ──────────────────────────────────────────────────
  const hasContextCollection = shouldShowContextCollection();
  return (
    <div className="content-tab-container">
      <div className="tab-heading">
        <h1>Start Remote Diagnostic Session</h1>
      </div>

      <div className="review-section">
        <h3>Review your selection:</h3>

        <div className="review-cards">
          <div className="review-card">
            <h4>System Details</h4>
            <div className="review-card-content">
              <div className="review-item">
                <span className="review-label">System ID:</span>
                <span className="review-value">{inputText}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Session Type:</span>
                <span className="review-value">{sessionMode?.label || "Start Immediately"}</span>
              </div>
              <div className="review-item">
                <span className="review-label">Open Ended:</span>
                <span className="review-value">{isOpenEnded ? "Yes" : "No"}</span>
              </div>
              {sessionMode?.value === "scheduled" && (
                <>
                  <div className="review-item">
                    <span className="review-label">Scheduling Mode:</span>
                    <span className="review-value">{schedulingMode?.label}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Scheduled Time:</span>
                    <span className="review-value">{new Date(scheduledTime).toLocaleString()}</span>
                  </div>
                </>
              )}
              {!isOpenEnded && (
                <div className="review-item">
                  <span className="review-label">Session Timer:</span>
                  <span className="review-value">{sessionTimer} mins</span>
                </div>
              )}
              <div className="review-item">
                <span className="review-label">Upload Frequency:</span>
                <span className="review-value">{uploadFrequency} seconds</span>
              </div>
            </div>
          </div>

          {rows.some(row => row.diagnosticData) && (
          <div className="review-card">
            <h4>Diagnostic Data (RD)</h4>
            <div className="review-card-content">
              {rows.filter(row => row.diagnosticData).map((row, index) => (
                <div key={index} className="diagnostic-item">
                  <div className="review-item">
                    <span className="review-label">Collector:</span>
                    <span className="review-value">{row.diagnosticData.label}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Command:</span>
                    <span className="review-value">{row.command?.label}</span>
                  </div>
                  {row.diagnosticData?.label === "ODL" && row.command?.value === "CONFIGURE_ENABLE" && (
                    <>
                      <div className="review-item">
                        <span className="review-label">Log Mask:</span>
                        <span className="review-value">{sessionLogMask?.label || "None"}</span>
                      </div>
                      {hasContextCollection && (
                        <>
                          <div className="review-item">
                            <span className="review-label">Max File Size:</span>
                            <span className="review-value">{maxFileSizeMb} MB</span>
                          </div>
                          {contextRules.filter(r => r.contextCollectionRule).map((r, ri) => (
                            <div key={ri} className="review-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                              <span className="review-label">Rule {ri + 1}:</span>
                              <span className="review-value" style={{ textAlign: 'left' }}>
                                {r.contextCollectionRule?.label} — {r.diagConfig?.label || 'No diag config'}
                              </span>
                            </div>
                          ))}
                          {contextRules.length === 0 && (
                            <div className="review-item">
                              <span className="review-label">Context Rules:</span>
                              <span className="review-value">None</span>
                            </div>
                          )}
                        </>
                      )}
                      {!hasContextCollection && (
                        <div className="review-item">
                          <span className="review-label">Max File Size:</span>
                          <span className="review-value">{row.maxFileSizeMb} MB</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          )}

          {/* AD section review */}
          {adPayload && (
            <div className="review-card">
              <h4>Telemetry Collector Config (AD)</h4>
              <div className="review-card-content">
                <div className="review-item">
                  <span className="review-label">Collector:</span>
                  <span className="review-value">AD WWAN Collector</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Command:</span>
                  <span className="review-value">{adPayload.adCommand}</span>
                </div>
                {adEntries.filter(e => e.adConfig).map((entry, ci) => (
                  <div key={ci} className="diagnostic-item">
                    <div className="review-item">
                      <span className="review-label">AD Config {ci + 1}:</span>
                      <span className="review-value">{entry.adConfig.label}</span>
                    </div>
                    {entry.rules.map((r, ri) => (
                      <div key={ri} className="review-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                        <span className="review-label">Rule {ri + 1} ({r.triggerType}):</span>
                        <span className="review-value" style={{ textAlign: 'left' }}>
                          {r.contextCollectionRule?.label || '—'}
                          {r.logCodeList ? ` — ${r.logCodeList.label}` : ''}
                        </span>
                      </div>
                    ))}
                    {entry.rules.length === 0 && (
                      <div className="review-item">
                        <span className="review-label">Rules:</span>
                        <span className="review-value" style={{ color: '#9ca3af' }}>None added</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="success-banner">
          <div className="success-message">{successMessage}</div>
          <button className="submit-button" onClick={handleStartNew}>
            Start New Session
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="error-banner">
          <div className="error-message">{errorMessage}</div>
        </div>
      )}

      {!successMessage && (
        <div className="review-actions">
          <button className="go-back-button" onClick={handleGoBack}>
            &lt; Go Back
          </button>
          <button className="submit-button" onClick={handleSubmit}>
            {errorMessage ? 'Try Again' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}

export default StartRemoteDebugTab;
